<template>
	<div class="map">
		<TMap
			ref="map"
			:bounds="bounds"
			:animate="false"
			@moveend="$emit('map:moveend',$event)"
		>
			<leaflet-pane paneName="order-markers" :zIndex="ordersIndex">
				<map-orders
					:modal="modal"
					:iconOptions="{iconSize: 10}"
					:tripDetailView="tripDetailsVisible"
				/>
			</leaflet-pane>
			<leaflet-pane paneName="trip-markers" :zIndex="tripsIndex">
				<map-trips
					v-if="trips"
					:tripDetailView="tripDetailsVisible"
					@showAddToTrip="$emit('showAddToTrip',$event)"
				/>
				<map-trip
					v-if="validTripMarker"
					:trip="tripDetails"
					@showAddToTrip="$emit('showAddToTrip',$event)"
				/>
			</leaflet-pane>
			<map-route
				:visible="true"
				:route="route"
				color="green"
				dashArray="3 6"
			/>
			<map-areas
				ref="searchAreas"
				:visible="false"
				:areas="mappedSearchAreas"
				:polygonOptions="{ color: '#10ca70', opacity: 0, fillColor: '#ffffff', fillOpacity: 0.2, interactive: false }"
				@layeradd="layerAdded"
			/>
			<map-areas
				visible
				:areas="includedAreas"
				:polygonOptions="{ color: '#1070ca', opacity: 0.4, fillColor: '#1070ca', fillOpacity: 0.2, interactive: false }"
			/>
			<map-areas
				visible
				:areas="excludedAreas"
				:polygonOptions="{ color: '#ec4c47', opacity: 0.4, fillColor: '#ec4c47', fillOpacity: 0.2, interactive: false }"
			/>
			<map-order v-if="selectedOriginalOrder" :order="selectedOriginalOrder" />
		</TMap>
		<div ref="modal">
			<map-modals
				v-if="modal"
				@showAddToTrip="$emit('showAddToTrip',$event)"
				@showAddToTransporter="$emit('showAddToTransporter',$event)"
			/>
		</div>
	</div>
</template>
<script>
import MapOrders from './mapMarkers/MapOrders.vue';
import MapOrder from './mapMarkers/MapOrder.vue';
import MapTrips from './mapMarkers/MapTrips.vue';
import MapModals from './MapModals.vue';
import MapRoute from './mapMarkers/MapRoute.vue';
//import MapTrucks from './mapMarkers/MapTrucks.vue';
import MapAreas from './mapMarkers/MapAreas.vue';
import MapTrip from './mapMarkers/MapTrip.vue';
import { mapDynamicGetters, mapDynamicState } from '@/core/mapDynamic';

export default {
	components: {
		MapModals,
		MapOrders,
		MapOrder,
		MapTrips,
		MapRoute,
		//MapTrucks,
		MapAreas,
		MapTrip
	},
	inject:['namespace'],
	data() {
		return {
			modal: null,
			includedAreas: [],
			excludedAreas: [],
			mappedSearchAreas: [],
			bounds: null,
			ordersIndex: 625
		};
	},
	computed: {
		...mapDynamicState('computedStoreNamespace',['tripsOnTopInMap', 'tripDetailsVisible']),
		...mapDynamicGetters('computedStoreNamespace',[
			'orders',
			'trips',
			'tripDetails',
			'orderFilter',
			'lockMap',
			'ordersLoading',
			'selectedOriginalOrder'
		]),
		computedStoreNamespace() {
			return `loadPlanning/${this.namespace.id}`;
		},
		route() {
		  return [];
		},
		searchAreas() {
			return this.orderFilter.searchAreas;
		},
		fromDespatchParty() {
			return this.orderFilter.fromDespatchParty;
		},
		toDeliveryParty() {
			return this.orderFilter.toDeliveryParty;
		},
		validTripMarker() {
			return this.tripDetails && this.tripDetails.type.internalCode !== 'external';
		},
		tripsIndex() {
			return this.ordersIndex + (this.tripsOnTopInMap ? 20 : -5);
		}
	},
	watch: {
		searchAreas: {
			immediate: true,
			handler(newVal) {
				let searchAreas = this.processSearchAreas(newVal);
				this.mappedSearchAreas = Object.freeze(searchAreas) || Object.freeze([]);
				this.processAreas();
			}
		},
		fromDespatchParty: {
			immediate: true,
			handler() {
				this.processAreas();
			}
		},
		toDeliveryParty: {
			immediate: true,
			handler() {
				this.processAreas();
			}
		},
		ordersLoading: {
			immediate: true,
			handler(newVal) {
				if (newVal === false) {
					this.refreshBounds();
				}
			}
		},
		lockMap: {
			immediate: true,
			handler(newVal, oldVal) {
				if (newVal !== oldVal) this.refreshBounds();
			}
		},
		tripDetailsVisible: {
			immediate: true,
			handler(newVal) {
				if (newVal)
					this.refreshBounds();
			}
		}
	},
	mounted() {
		this.modal = this.$refs['modal'];
		this.refreshBounds();
	},
	methods: {
		layerAdded() {
			if (this.lockMap) this.refreshBounds();
		},
		refreshBounds() {
			if (this.lockMap && this.$refs.searchAreas) {
				this.bounds = this.$refs.searchAreas.getBounds();
			} else {
				let minLat = Number.POSITIVE_INFINITY;
				let maxLat = Number.NEGATIVE_INFINITY;
				let minLng = Number.POSITIVE_INFINITY;
				let maxLng = Number.NEGATIVE_INFINITY;
				if (this.namespace.tripId && this.tripDetails) {
					minLat = Math.min(minLat, this.tripDetails.fromAddress.latitude || Number.POSITIVE_INFINITY);
					maxLat = Math.max(maxLat, this.tripDetails.toAddress.latitude || Number.NEGATIVE_INFINITY);
					minLng = Math.min(minLng, this.tripDetails.fromAddress.longitude || Number.POSITIVE_INFINITY);
					maxLng = Math.max(maxLng, this.tripDetails.toAddress.longitude || Number.NEGATIVE_INFINITY);
				} else if (this.tripDetailsVisible && this.tripDetails) {
					minLat = Math.min(minLat, this.tripDetails.fromAddress.latitude || Number.POSITIVE_INFINITY);
					maxLat = Math.max(maxLat, this.tripDetails.fromAddress.latitude || Number.NEGATIVE_INFINITY);
					minLng = Math.min(minLng, this.tripDetails.fromAddress.longitude || Number.POSITIVE_INFINITY);
					maxLng = Math.max(maxLng, this.tripDetails.fromAddress.longitude || Number.NEGATIVE_INFINITY);

					for (let order of this.tripDetails.orders) {
						if (!order.despatchParty) continue;
						minLat = Math.min(minLat, order.despatchParty.latitude || Number.POSITIVE_INFINITY);
						maxLat = Math.max(maxLat, order.despatchParty.latitude || Number.NEGATIVE_INFINITY);
						minLng = Math.min(minLng, order.despatchParty.longitude || Number.POSITIVE_INFINITY);
						maxLng = Math.max(maxLng, order.despatchParty.longitude || Number.NEGATIVE_INFINITY);
					}
				} else {
					for (let order of this.orders) {
						if (!order.despatchParty) continue;
						minLat = Math.min(minLat, order.despatchParty.latitude || Number.POSITIVE_INFINITY);
						maxLat = Math.max(maxLat, order.despatchParty.latitude || Number.NEGATIVE_INFINITY);
						minLng = Math.min(minLng, order.despatchParty.longitude || Number.POSITIVE_INFINITY);
						maxLng = Math.max(maxLng, order.despatchParty.longitude || Number.NEGATIVE_INFINITY);
					}
				}

				if ( minLat === Number.POSITIVE_INFINITY ||
					maxLat === Number.NEGATIVE_INFINITY ||
					minLng === Number.POSITIVE_INFINITY ||
					maxLng === Number.NEGATIVE_INFINITY)
					this.bounds = null;
				else {
					if (minLat === maxLat) maxLat = maxLat + 1e-6;
					if (minLng === maxLng) maxLng = maxLng + 1e-6;
					this.bounds = [[ minLat,minLng ],[ maxLat,maxLng ]];
				}
			}
			this.$refs.map && this.$refs.map.zoomToBounds();
		},
		processAreas() {
			let included = [];
			let excluded = [];
			this.searchAreas.forEach(polygon => polygon.type === 'polygon-filter' && polygon.op === 'NOT' ? excluded.push(polygon) : included.push(polygon));
			this.fromDespatchParty.forEach(polygon => polygon.type === 'polygon-filter' && polygon.op === 'NOT' ? excluded.push(polygon) : included.push(polygon));
			this.toDeliveryParty.forEach(polygon => polygon.type === 'polygon-filter' && polygon.op === 'NOT' ? excluded.push(polygon) : included.push(polygon));
			this.includedAreas = Object.freeze(included) || [];
			this.excludedAreas = Object.freeze(excluded) || [];
		},
		processSearchAreas(data) {
			return data.filter((polygon) => polygon.type === 'polygon-filter');
		},
	}
};
</script>

<style scoped>

.map {
	display: block;
	width: 100%;
	height: 100%;
}

</style>
